import axios, { AxiosInstance, AxiosResponse } from 'axios';

// Resolve API base URL safely so we never use http:// when the page
// itself is loaded over https:// (avoids mixed-content errors when
// the app is served behind an HTTPS reverse proxy).
const rawBaseUrl = process.env.REACT_APP_API_URL || '/api';

function resolveBaseUrl(): string {
  if (typeof window === 'undefined') {
    // Server-side rendering: use the configured value
    return rawBaseUrl || '/api';
  }

  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  
  // If accessing via localhost (development), use localhost backend
  if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
    return 'http://localhost:7905/api';
  }
  
  // ALWAYS use relative path for production domains to ensure proper proxy handling
  // This will automatically use the same protocol and host as the current page
  return '/api';
}

const API_BASE_URL = resolveBaseUrl();

// Debug logging
if (typeof window !== 'undefined') {
  console.log('🔧 API Configuration:', {
    currentUrl: window.location.href,
    currentHost: window.location.hostname,
    currentProtocol: window.location.protocol,
    resolvedApiBaseUrl: API_BASE_URL,
    rawBaseUrl: rawBaseUrl
  });
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and ensure HTTPS
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Ensure Content-Type is always set for POST requests
      if (config.method === 'post' && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
      
      // AGGRESSIVE HTTPS ENFORCEMENT
      if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
        // Force baseURL to use relative path for HTTPS pages
        if (config.baseURL && (config.baseURL.startsWith('http://') || config.baseURL.includes('stocks.bhatia.ch'))) {
          config.baseURL = '/api';
          console.warn('🔒 FORCED baseURL to relative path for HTTPS:', config.baseURL);
        }
        
        // Also check the full URL construction
        const fullUrl = (config.baseURL || '') + (config.url || '');
        if (fullUrl.startsWith('http://')) {
          config.baseURL = '/api';
          console.warn('🔒 FORCED baseURL due to HTTP in full URL:', fullUrl);
        }
      }
      
      return config;
    });

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear token and redirect to login only if we're not already on login page
          localStorage.removeItem('token');
          if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
            // Store current path to redirect back after login
            localStorage.setItem('redirectPath', window.location.pathname);
          window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Method to check if user is authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  // Method to get current token
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  // Method to set token
  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  // Method to clear token
  clearToken(): void {
    localStorage.removeItem('token');
  }

  async get<T>(url: string): Promise<T> {
    // Debug: Log the actual request URL
    const fullUrl = this.client.defaults.baseURL + url;
    console.log('🌐 API GET Request:', {
      requestUrl: url,
      baseURL: this.client.defaults.baseURL,
      fullUrl: fullUrl,
      protocol: fullUrl.startsWith('https://') ? 'HTTPS' : 'HTTP'
    });
    
    const response: AxiosResponse<T> = await this.client.get(url);
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url);
    return response.data;
  }

  async deleteWithData<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, { data });
    return response.data;
  }

  async uploadFile<T>(url: string, file: File, additionalData?: any): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const response: AxiosResponse<T> = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const apiService = new ApiService(); 

// URL Corrections Types
export interface UrlCorrection {
  id: string;
  stock_symbol: string;
  original_url: string;
  corrected_url?: string;
  error_message?: string;
  error_count: number;
  is_resolved: boolean;
  is_ignored: boolean;
  ignored_at?: string;
  last_error_at: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
}

export interface UrlCorrectionsResponse {
  corrections: UrlCorrection[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface UrlCorrectionStatsResponse {
  total_corrections: number;
  unresolved_corrections: number;
  resolved_corrections: number;
  ignored_corrections: number;
  top_error_symbols: Array<{
    symbol: string;
    error_count: number;
  }>;
}

export interface UrlCorrectionUpdateResponse {
  message: string;
  correction: UrlCorrection;
}

export interface UrlCorrectionDeleteResponse {
  message: string;
}

export interface BulkResolveResponse {
  message: string;
  updated_count: number;
  errors: string[];
}

// URL Corrections API
export const getUrlCorrections = (page = 1, perPage = 20, status = 'unresolved'): Promise<UrlCorrectionsResponse> => {
  return apiService.get<UrlCorrectionsResponse>(`/stocks/url-corrections?page=${page}&per_page=${perPage}&status=${status}`);
};

export const updateUrlCorrection = (correctionId: string, correctedUrl: string): Promise<UrlCorrectionUpdateResponse> => {
  return apiService.put<UrlCorrectionUpdateResponse>(`/stocks/url-corrections/${correctionId}`, {
    corrected_url: correctedUrl
  });
};

export const deleteUrlCorrection = (correctionId: string): Promise<UrlCorrectionDeleteResponse> => {
  return apiService.delete<UrlCorrectionDeleteResponse>(`/stocks/url-corrections/${correctionId}`);
};

export const bulkResolveUrlCorrections = (corrections: Array<{id: string, corrected_url: string}>): Promise<BulkResolveResponse> => {
  return apiService.post<BulkResolveResponse>('/stocks/url-corrections/bulk-resolve', {
    corrections
  });
};

export const getUrlCorrectionStats = (): Promise<UrlCorrectionStatsResponse> => {
  return apiService.get<UrlCorrectionStatsResponse>('/stocks/url-corrections/stats');
};

export const ignoreUrlCorrection = (correctionId: string): Promise<UrlCorrectionUpdateResponse> => {
  return apiService.post<UrlCorrectionUpdateResponse>(`/stocks/url-corrections/${correctionId}/ignore`, {});
};

export const unignoreUrlCorrection = (correctionId: string): Promise<UrlCorrectionUpdateResponse> => {
  return apiService.post<UrlCorrectionUpdateResponse>(`/stocks/url-corrections/${correctionId}/unignore`, {});
}; 
